import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { FunnelSimple, ArrowsClockwise, Warning, Flag, CheckCircle, X } from "@phosphor-icons/react";

const STAGES = ["osint","validation","test_purchase","enforcement","closed"];
const STAGE_LABELS = { osint:"OSINT", validation:"Target validation", test_purchase:"Test purchase", enforcement:"Enforcement", closed:"Closure" };

export default function InvestigationTracker() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ stage:"", escalated:"", stale_hours:"", mine:false });
  const [updating, setUpdating] = useState(null);
  const nav = useNavigate();

  const load = () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (filter.stage) q.set("stage", filter.stage);
    if (filter.escalated) q.set("escalated", filter.escalated);
    if (filter.stale_hours) q.set("stale_hours", filter.stale_hours);
    if (filter.mine) q.set("mine", "true");
    api.get(`/nfor/tracker?${q}`)
      .then(r => setRows(r.data?.rows || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div className="p-6 space-y-4" data-testid="investigation-tracker">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Investigation tracker</div>
          <h1 className="font-display font-bold text-3xl mt-1">Live SPAN matters</h1>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">Excel-equivalent live view · CEO/COO see all · Ops sees assigned.</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="filter-stage" value={filter.stage} onChange={e => setFilter({...filter, stage:e.target.value})} className="text-[12px] hairline px-2 py-1">
            <option value="">All stages</option>
            {STAGES.map(s => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
          </select>
          <select data-testid="filter-escalated" value={filter.escalated} onChange={e => setFilter({...filter, escalated:e.target.value})} className="text-[12px] hairline px-2 py-1">
            <option value="">All</option>
            <option value="true">Escalated only</option>
          </select>
          <select data-testid="filter-stale" value={filter.stale_hours} onChange={e => setFilter({...filter, stale_hours:e.target.value})} className="text-[12px] hairline px-2 py-1">
            <option value="">All freshness</option>
            <option value="48">No update 48h+</option>
            <option value="72">No update 72h+</option>
          </select>
          <label className="text-[12px] hairline px-2 py-1 cursor-pointer flex items-center gap-1">
            <input type="checkbox" checked={filter.mine} onChange={e => setFilter({...filter, mine:e.target.checked})} data-testid="filter-mine"/> My cases
          </label>
          <button data-testid="tracker-refresh" onClick={load} className="text-[11px] hairline px-2 py-1 hover:bg-[var(--span-surface-3)]"><ArrowsClockwise size={11} className="inline"/></button>
          <Link to="/cases/nfor" className="span-btn text-[11px]">+ New NFOR</Link>
        </div>
      </div>

      <div className="card-flat p-0 overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-[var(--span-surface-3)] text-[9px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-2 py-2">#</th>
              <th className="text-left px-2 py-2">Client</th>
              <th className="text-left px-2 py-2">File ref</th>
              <th className="text-left px-2 py-2">Product</th>
              <th className="text-left px-2 py-2">Stage</th>
              <th className="text-left px-2 py-2">RM</th>
              <th className="text-left px-2 py-2">MD</th>
              <th className="text-left px-2 py-2">Date contact</th>
              <th className="text-left px-2 py-2">Contact</th>
              <th className="text-left px-2 py-2">Target</th>
              <th className="text-left px-2 py-2">Location</th>
              <th className="text-left px-2 py-2 min-w-[180px]">Today's update</th>
              <th className="text-left px-2 py-2">Next action</th>
              <th className="text-left px-2 py-2">Escalated</th>
              <th className="text-left px-2 py-2">COO</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={16} className="px-3 py-6 text-center text-[var(--span-text-2)]">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={16} className="px-3 py-6 text-center text-[var(--span-text-2)]">No active matters match the filters.</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={r.case_id} className="border-t hairline hover:bg-[var(--span-surface-3)] cursor-pointer"
                  data-testid={`tracker-row-${r.case_id}`}
                  onClick={(e) => { if (e.target.tagName !== "BUTTON") nav(`/cases/${r.case_id}`); }}>
                <td className="px-2 py-1.5 text-[var(--span-text-2)]">{i+1}</td>
                <td className="px-2 py-1.5 font-bold">{r.client_name}</td>
                <td className="px-2 py-1.5 font-mono text-[10px]">{r.our_file_reference || r.case_code}</td>
                <td className="px-2 py-1.5 truncate max-w-[120px]" title={r.product_at_risk}>{r.product_at_risk || "—"}</td>
                <td className="px-2 py-1.5">
                  <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 bg-[var(--span-surface-3)]">{STAGE_LABELS[r.current_stage] || r.current_stage || "—"}</span>
                </td>
                <td className="px-2 py-1.5">
                  {r.rm_initials ? (
                    <span className="font-mono text-[9px] tracking-[0.18em] px-1.5 py-0.5 bg-sky-500/10 text-sky-300 border border-sky-500/30" title={r.rm_name}>{r.rm_initials}</span>
                  ) : <span className="text-[var(--span-text-2)]">—</span>}
                </td>
                <td className="px-2 py-1.5">
                  {r.md_initials ? (
                    <span className="font-mono text-[9px] tracking-[0.18em] px-1.5 py-0.5 bg-amber-500/10 text-amber-300 border border-amber-500/30" title={r.md_name}>{r.md_initials}</span>
                  ) : <span className="text-[var(--span-text-2)]">—</span>}
                </td>
                <td className="px-2 py-1.5 text-[var(--span-text-2)]">{fmtDate(r.date_of_contact)}</td>
                <td className="px-2 py-1.5 truncate max-w-[100px]">{r.client_contact_persons || "—"}</td>
                <td className="px-2 py-1.5 truncate max-w-[120px]" title={r.target_entity}>{r.target_entity || "—"}</td>
                <td className="px-2 py-1.5 truncate max-w-[100px]" title={r.locations_affected}>{r.locations_affected || "—"}</td>
                <td className="px-2 py-1.5">
                  {r.latest_update ? (
                    <div className="text-[11px] truncate max-w-[180px]" title={r.latest_update}>{r.latest_update}</div>
                  ) : (
                    <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 ${r.staleness === "red" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>No update today</span>
                  )}
                </td>
                <td className="px-2 py-1.5 truncate max-w-[120px]" title={r.next_action}>{r.next_action || "—"}</td>
                <td className="px-2 py-1.5">
                  {r.escalated && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 bg-red-50 text-red-700"><Flag size={9} className="inline mr-0.5"/>Yes</span>}
                </td>
                <td className="px-2 py-1.5">
                  {r.coo_approved ? <CheckCircle size={12} weight="fill" className="text-emerald-600"/> : <span className="text-[10px] text-[var(--span-text-2)]">—</span>}
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap">
                  <button data-testid={`update-btn-${r.case_id}`} onClick={(e) => { e.stopPropagation(); setUpdating(r); }} className="text-[10px] hairline px-2 py-1 hover:bg-emerald-50">Update</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {updating && (
        <DailyUpdateSlideover row={updating} onClose={() => setUpdating(null)} onSaved={() => { setUpdating(null); load(); }}/>
      )}
    </div>
  );
}

function DailyUpdateSlideover({ row, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [next, setNext] = useState("");
  const [escalate, setEscalate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState("");

  const onPickFiles = (e) => {
    const incoming = Array.from(e.target.files || []);
    const valid = incoming.filter(f => f.size <= 20 * 1024 * 1024);
    if (valid.length < incoming.length) alert("Some files exceed 20 MB and were skipped.");
    setFiles(prev => [...prev, ...valid]);
    e.target.value = "";
  };
  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/cases/${row.case_id}/daily-update`, { text, next_action: next, escalate });
      // Phase 22 — write to case_updates so it appears on the Updates tab and we can attach files
      let updateId = null;
      try {
        const { data } = await api.post(`/cases/${row.case_id}/updates`, { text, channel: "in_app" });
        updateId = data?.update_id || data?.update?.update_id;
      } catch (_) {
        // fallback: tracker shortcut (no id returned, files will be skipped)
        try { await api.post(`/tracker/today-update`, { case_id: row.case_id, text }); } catch (_e2) { /* best-effort */ }
      }
      // Upload attachments if any
      if (updateId && files.length) {
        for (let i = 0; i < files.length; i++) {
          setProgress(`Uploading ${i + 1}/${files.length}: ${files[i].name}`);
          const fd = new FormData();
          fd.append("file", files[i]);
          try {
            await api.post(`/cases/${row.case_id}/updates/${updateId}/attach`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (e) { console.warn("Attach failed:", e); }
        }
        setProgress("");
      }
      onSaved();
    } finally { setBusy(false); setProgress(""); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-[var(--surface,#fff)] w-full max-w-md h-full p-6 space-y-3 overflow-auto" data-testid="daily-update-slideover">
        <div className="flex items-start justify-between">
          <div>
            <div className="label-eyebrow">Daily update</div>
            <h2 className="font-display font-bold text-lg mt-1">{row.our_file_reference}</h2>
            <div className="text-[11px] text-[var(--span-text-2)] mt-0.5">{row.client_name} · {row.title}</div>
          </div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Today's update *</label>
          <textarea data-testid="update-text" rows={5} value={text} onChange={e => setText(e.target.value)} className="w-full hairline px-2 py-1.5 text-[12px]"/>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Next action planned</label>
          <input data-testid="update-next" value={next} onChange={e => setNext(e.target.value)} className="w-full hairline px-2 py-1.5 text-[12px]" placeholder="e.g. Test purchase on Wed"/>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Attachments (images, xlsx, csv, pdf · ≤20MB each)</label>
          <input
            type="file"
            multiple
            accept=".png,.jpg,.jpeg,.webp,.gif,.pdf,.xlsx,.xls,.csv,.docx,.doc,.pptx,.txt,.mp4,.mov"
            onChange={onPickFiles}
            data-testid="update-file-input"
            className="block w-full text-[12px] file:mr-2 file:py-1 file:px-2 file:border-0 file:text-[11px] file:bg-[var(--surface-2)] file:rounded"
          />
          {files.length > 0 && (
            <div className="mt-2 space-y-1" data-testid="update-files-list">
              {files.map((f, i) => (
                <div key={i} className="flex items-center justify-between text-[11px] bg-[var(--surface-2)] px-2 py-1 rounded">
                  <span className="truncate flex-1 font-mono">{f.name} <span className="text-[var(--text-muted)]">({(f.size / 1024).toFixed(0)} KB)</span></span>
                  <button onClick={() => removeFile(i)} className="text-[var(--status-danger)] ml-2"><X size={11}/></button>
                </div>
              ))}
            </div>
          )}
          {progress && <div className="text-[11px] text-[var(--brand-primary)] mt-1">{progress}</div>}
        </div>
        <label className="flex items-center gap-2 text-[12px]" data-testid="update-escalate-toggle">
          <input type="checkbox" checked={escalate} onChange={e => setEscalate(e.target.checked)}/>
          Escalate this to CEO
        </label>
        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="text-[11px] hairline px-3 py-1.5">Cancel</button>
          <button data-testid="update-save" disabled={busy || !text.trim()} onClick={save} className="span-btn">{busy ? "Saving…" : "Save update"}</button>
        </div>
      </div>
    </div>
  );
}
